import { Component, OnDestroy } from '@angular/core';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AbstractControl } from '@angular/forms';
import { Subject } from 'rxjs';

/**
 * Base Leatherman component
 */
@Component({
  selector: 'lm-base-component',
  template: 'No UI Here'
})
export class BaseComponent implements OnDestroy {
  /** Boolean indicating whether the component has been initialized */
  public isInitialized = false;

  /** Boolean indicating whether the component's data is loading */
  public isLoading = false;

  /** An RXJS subject used to destroy subscriptions when the component is destroyed */
  public destroySubject$: Subject<void> = new Subject();

  // constructor
  constructor() { }

  /**
   * Trigger the destroy subject  when the component is destroyed
   */
  public ngOnDestroy() {
    this.destroySubject$.next();
  }

  protected decodeURIComponent(uriComponent: string): string {
    if (!uriComponent) {
      return uriComponent;
    }
    return decodeURIComponent(uriComponent);
  }

  protected encodeURIComponent(uriComponent: string): string {
    if (!uriComponent) {
      return uriComponent;
    }
    return encodeURIComponent(uriComponent);
  }

  /**
   * Subscribe to a subject
   * @param subject$ - The subject to subscribe to
   * @param callback - The callback method to call when the subject is triggered
   * @returns Void
   */
  protected subscribeSubject<T>(
    subject$: Subject<T>,
    callback: (result: any) => void
  ) {
    subject$.pipe(takeUntil(this.destroySubject$)).subscribe(async result => {
      await callback(result);
    });
  }

  /**
   * Subscribe to a subject synchronously
   * @param subject$ - The subject to subscribe to
   * @param callback - The callback method to call when the subject is triggered
   * @returns Void
   */
  protected subscribeSubjectSync<T>(
    subject$: Subject<T>,
    callback: (result: any) => void
  ) {
    subject$.pipe(takeUntil(this.destroySubject$)).subscribe(result => {
      callback(result);
    });
  }

  /**
   * Subscribe to value change events for a control
   * @param control - The control to listen to
   * @param callback - The functiion to call when the control value changes
   */
  protected subscribeValueChanges(
    control: AbstractControl,
    callback: (result: any) => void
  ) {
    control.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged()
      )
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(async result => {
        await callback(result);
      });
  }
}
